/**
 * Blog content is rendered in 3 "chunks", with 2 DreamSearch card sections
 * injected between the chunks. This function takes Markdown-formatted
 * content and splits it into 3 Markdown-formatted content chunks.
 *
 * The general methodology is that the content is split into sections, defined
 * as a heading and all content that follows it, up to the next heading. (The
 * type of heading does not matter.)
 *
 * Then, the top chunk contains the first section and the middle/bottom chunks
 * split the remaining sections.
 *
 * If we fail to split the content into 3 chunks (for example, there aren't
 * enough headings), we will attempt to split it into the top and middle
 * chunks (leaving the bottom chunk) blank.
 *
 * Some blog posts (mostly legacy) have no headings at all. In this case, we
 * split the content by paragraph.
 *
 * If all else fails, we return the entire content as-is in the middle chunk.
 */
export const separateAndFormatBlogContent = (content: string): [string, string, string] => {
  /*
  Split content into sections by looking for `\n` and `#` -- that is, any number
  of new lines followed by a hash (which all Markdown headings start with).
   */
  const sections = content
    .split(new RegExp("[\n]+(?=#)", "g"))
    .map((section) => section.trim())
    .filter(Boolean);

  if (sections.length === 1) {
    /*
    If there aren't headings, we split by paragraph.
     */
    const paragraphs = content
      .split("\n\n")
      .map((section) => section.trim())
      .filter(Boolean);

    /*
    If there aren't at least 4 paragraphs, we give up on this strategy since
    either something has gone wrong or the content is too short to meaningfully
    chunk.
     */
    if (paragraphs.length <= 4) {
      return ["", content, ""];
    }

    const middleSplitIdx = Math.floor(paragraphs.length / 2);

    return [
      paragraphs.slice(0, middleSplitIdx).join("\n\n"),
      paragraphs.slice(middleSplitIdx).join("\n\n"),
      "",
    ];
  }

  /*
  Special handling: we give up and return unmodified content in the middle
  chunk if there are no sections (insane error), or 2 sections
  (not enough to meaningfully chunk, and might be risky to chunk using the
  above "split by paragraph" strategy).
   */
  if (sections.length <= 2) {
    return ["", content, ""];
  }

  /*
  Special handling: we only return content in the top and middle chunk if there
  are 3 or 4 sections. The section goes in the top.
   */
  if (sections.length <= 4) {
    return [sections[0], sections.slice(1).join("\n\n"), ""];
  }

  const middleBottomSplitIdx = Math.floor((sections.length - 1) / 2) + 1;
  return [
    sections[0],
    sections.slice(1, middleBottomSplitIdx + 1).join("\n\n"),
    sections.slice(middleBottomSplitIdx + 1).join("\n\n"),
  ];
};
